import { useEffect, useState } from "react";
import { CheckCircle } from "react-bootstrap-icons";
import { toast } from "react-toastify";

import {
  Actions,
  CloseIcon,
  CloseIconWrap,
  ModalContainer,
  ModalContent,
  ModalFooterWrapper,
  ModalHeading,
  ModalHeadingWrapper,
  ModalOverlay,
  ModalWrapper,
  OverviewContent,
} from "../styles";
import { Button } from "../../Button";
import { MultiSelect } from "../../Inputs/CustomMultiSelect/MultiSelect";
import { CustomInput } from "../../Inputs/CustomInput";
import {
  ContentWrapper,
  InfoCircleIcon,
  SelectBlock,
  SelectBlockLabel,
  SelectBlockTitle,
} from "./style";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedTeam } from "../../../store/selectors/userManagement";
import { requestAllTeams, requestEditTeam } from "../../../store/slices/teams";
import { getTeams } from "../../../store/selectors/teams";
import { getAllUsers } from "../../../store/selectors/users";
import { requestAllUsers, setAllUsers } from "../../../store/slices/users";
import { getAllDatasets } from "../../../store/selectors/datasets";
import { requestGetAllDatasets } from "../../../store/slices/datasets";

type ModalProps = {
  onClose: () => void;
};
interface Form {
  name: string;
  managers: string[];
  programs: string[];
}

export const EditTeam = ({ onClose }: ModalProps) => {
  const selectedTeamId = useSelector(getSelectedTeam);
  const teams = useSelector(getTeams);
  const users = useSelector(getAllUsers);
  const datasets = useSelector(getAllDatasets);
  const dispatch = useDispatch();

  const getData = () => {
    if (!teams.length) {
      dispatch(requestAllTeams({}));
    }
    if (!users.length) {
      dispatch(requestAllUsers({ limit: 1000 }));
    }
    dispatch(requestGetAllDatasets({ defaultDatasets: true }));
  };

  const unmountData = () => {
    dispatch(setAllUsers([]));
  };

  useEffect(() => {
    getData();
    return () => {
      unmountData();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options =
    datasets?.items?.map((el) => ({ value: el.id, option: el.name })) || [];

  const team = teams.find((el) => el.id === selectedTeamId);

  const defaultForm: Form = {
    name: team?.name || "",
    managers:
      team?.members
        ?.filter((el) => el.role === "manager")
        .map((el) => el.email) || [],
    programs: team?.datasets?.map((el) => el.id) || [],
  };

  const [isClosing, setIsClosing] = useState(false);
  const [form, setForm] = useState(defaultForm);

  const handleOnClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 400);
  };

  const handleSave = () => {
    handleOnClose();
    if (!team?.id) {
      toast.error("Team not found");
      return;
    }
    dispatch(
      requestEditTeam({
        id: team?.id,
        name: form.name,
        datasetIds: form.programs,
        managerEmails: form.managers,
      })
    );
  };

  return (
    <ModalOverlay $centred $isClosing={isClosing} onClick={handleOnClose}>
      <ModalWrapper
        $isClosing={isClosing}
        $centred
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeadingWrapper $disableSeparator={false}>
          <ModalContainer>
            <ModalHeading>Edit Team</ModalHeading>
            <CloseIconWrap>
              <CloseIcon onClick={handleOnClose}></CloseIcon>
            </CloseIconWrap>
          </ModalContainer>
        </ModalHeadingWrapper>
        <OverviewContent>
          <ModalContent>
            <ContentWrapper>
              <CustomInput
                name="name"
                value={form.name}
                type={"text"}
                onChange={(e: string) => {
                  setForm({ ...form, name: e });
                }}
                placeholder={"Name"}
                disablePaddings
                size={"base"}
              />
              <SelectBlock>
                <SelectBlockTitle>Programs</SelectBlockTitle>
                <MultiSelect
                  placeholder="Select programs"
                  value={form.programs}
                  disableAddByEnter
                  onSelect={(item) => {
                    setForm({ ...form, programs: item.map((el) => el.value) });
                  }}
                  options={options}
                />
              </SelectBlock>
              <SelectBlock>
                <SelectBlockTitle>Managers</SelectBlockTitle>
                <MultiSelect
                  placeholder="Managers"
                  limit={50}
                  limitMessage="You can add maximum 50 members to team"
                  value={form.managers}
                  errors={["@", "."]}
                  options={
                    users
                      ?.filter(
                        (el) => !form.managers.find((m) => m === el.email)
                      )
                      .map((el) => ({
                        value: el.email,
                        option: el.name,
                        label: el.email,
                        avatar: true,
                      })) || []
                  }
                  onSelect={(item) => {
                    setForm({
                      ...form,
                      managers: item.map((el) =>
                        typeof el === "string" ? el : el.value || String(el)
                      ),
                    });
                  }}
                />
                <SelectBlockLabel>
                  <InfoCircleIcon />A team can have up to 50 members.
                </SelectBlockLabel>
              </SelectBlock>
            </ContentWrapper>
          </ModalContent>
        </OverviewContent>
        <ModalFooterWrapper>
          <Button
            name="Cancel"
            onClick={handleOnClose}
            variant="neutral"
            size="medium"
          />
          <Actions>
            <Button
              name="Save"
              onClick={handleSave}
              variant={true ? "secondary" : "disabled"}
              disabled={false}
              size="medium"
              icon={<CheckCircle />}
            />
          </Actions>
        </ModalFooterWrapper>
      </ModalWrapper>
    </ModalOverlay>
  );
};
